import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { t } from '@lingui/core/macro';
import { Download, Eye, GripVertical, Loader2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { downloadProgramFile } from '@/api/programFiles';
import { ProgramFilePreviewDialog } from '@/components/ProgramFilePreviewDialog';
import { Button } from '@/components/ui/button';
import type { ProgramFileInfo } from '@/types/api';
import { apiErrorMessage } from '@/utils/apiError';
import { downloadBlob } from '@/utils/download';
import { cn } from '@/utils/cn';

interface SortableFileRowProps {
  file: ProgramFileInfo;
  onDelete: (id: string, filename: string) => void;
  deletingProgramId: string | null;
  disabled?: boolean;
}

const useDownloadProgramFile = (file: ProgramFileInfo) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async (): Promise<void> => {
    setIsDownloading(true);
    try {
      const blob = await downloadProgramFile(file.id);
      downloadBlob(URL.createObjectURL(blob), file.original_filename);
    } catch (e) {
      toast.error(apiErrorMessage(e, t`Failed to download program file.`));
    } finally {
      setIsDownloading(false);
    }
  };

  return { isDownloading, handleDownload };
};

export const SortableFileRow = ({
  file,
  onDelete,
  deletingProgramId,
  disabled,
}: SortableFileRowProps): React.ReactElement => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: file.id,
    disabled,
  });

  const { isDownloading, handleDownload } = useDownloadProgramFile(file);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr ref={setNodeRef} style={style} className={cn('hover:bg-slate-50', isDragging && 'bg-slate-50 z-50')}>
      <td className="w-0 px-4 py-2 pr-2">
        <div
          className={cn(
            disabled
              ? 'cursor-not-allowed text-slate-300'
              : 'cursor-grab text-slate-400 hover:text-slate-600 active:cursor-grabbing'
          )}
          {...(!disabled && { ...attributes, ...listeners })}
          title={disabled ? t`Simulation in progress - modifications disabled` : t`Drag to reorder`}
        >
          <GripVertical size={14} />
        </div>
      </td>
      <td className="px-4 py-2 font-mono text-slate-800">{file.original_filename}</td>
      <td>
        <div className="flex flex-wrap gap-1">
          {file.tools.map((tool) => (
            <span key={tool} className="badge-slate">
              {tool}
            </span>
          ))}
        </div>
      </td>
      <td className="px-4 py-2 text-right">
        <div className="flex items-center justify-end gap-1">
          <ProgramFilePreviewDialog
            file={file}
            trigger={
              <Button type="button" size="icon" variant="ghost" className="text-slate-500" title={t`Preview`}>
                <Eye />
              </Button>
            }
          />
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="text-slate-500"
            onClick={handleDownload}
            disabled={isDownloading}
            title={t`Download`}
          >
            {isDownloading ? <Loader2 className="animate-spin" /> : <Download />}
          </Button>
          <span title={disabled ? t`Simulation in progress - modifications disabled` : t`Remove`} className={disabled ? 'inline-flex cursor-not-allowed' : 'inline-flex'}>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="text-slate-500 hover:text-red-600"
              onClick={() => onDelete(file.id, file.original_filename)}
              disabled={disabled === true || deletingProgramId === file.id || isDownloading}
            >
              {deletingProgramId === file.id ? <Loader2 className="animate-spin" /> : <Trash2 />}
            </Button>
          </span>
        </div>
      </td>
    </tr>
  );
};
